import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import { STARTED_BOOKING_DATA_KEY } from '../constants';
import { setBookingDataToLocalStorage } from '../helpers/setBookingDataToLocalStorage';

export type BookingUnitViewMode = 'list' | 'map';

const parseUnitViewMode = (value: string | undefined): BookingUnitViewMode | null => {
  if (value === 'map' || value === 'list') {
    return value;
  }

  return null;
};

const readStoredUnitViewMode = (): BookingUnitViewMode | null => {
  try {
    const raw = localStorage.getItem(STARTED_BOOKING_DATA_KEY);

    if (!raw) {
      return null;
    }

    return parseUnitViewMode(new URLSearchParams(raw).get('unitViewMode') ?? undefined);
  } catch {
    return null;
  }
};

const readUrlUnitViewMode = (search: string): BookingUnitViewMode | null => {
  return parseUnitViewMode(new URLSearchParams(search).get('unitViewMode') ?? undefined);
};

interface UseBookingUnitViewModeResult {
  viewMode: BookingUnitViewMode;
  setMapMode: () => void;
  setListMode: () => void;
}

export const useBookingUnitViewMode = (): UseBookingUnitViewModeResult => {
  const navigate = useNavigate();
  const { parsedQuery } = useQueryString();

  const [viewMode, setViewMode] = useState<BookingUnitViewMode>(() => {
    return readUrlUnitViewMode(window.location.search) ?? readStoredUnitViewMode() ?? 'list';
  });

  useEffect(() => {
    const next = parseUnitViewMode(parsedQuery.unitViewMode);

    if (next) {
      setViewMode((prev) => (prev === next ? prev : next));
    }
  }, [parsedQuery.unitViewMode]);

  const setMapMode = useCallback(() => {
    setViewMode('map');
    setBookingDataToLocalStorage({ unitViewMode: 'map' }, navigate);
  }, [navigate]);

  const setListMode = useCallback(() => {
    setViewMode('list');
    setBookingDataToLocalStorage({ unitViewMode: 'list' }, navigate);
  }, [navigate]);

  return {
    viewMode,
    setMapMode,
    setListMode,
  };
};
